import { useEffect, useState } from "react";
import { Package, Eye, EyeSlash, ArrowUp, ArrowDown, FloppyDisk, Prohibit, PencilSimple, Check, ArrowClockwise, Plus, Trash } from "@phosphor-icons/react";
import ProductPicker from "@/pages/admin/ProductPicker";
import BundleSectionSettings from "@/pages/admin/BundleSectionSettings";
import CollapsibleSection from "@/components/admin/CollapsibleSection";

/**
 * Admin · Bundles panel.
 * Simple drag-drop-free reorder (arrow buttons), inline edit of name/badge/
 * price/description/cover/free-gift, per-slot eligible-product editor, and
 * show/hide toggle. Also edits the section heading, subheading, placement,
 * hidden state, and logo URL.
 */
export default function BundlesPanel({ token, backend }) {
  const [bundles, setBundles] = useState([]);
  const [section, setSection] = useState({ heading: "", subheading: "", placement: "above_catalog", hidden: false, logo_url: "", featured_slug: "", featured_slugs: [] });
  const [catalog, setCatalog] = useState({}); // product_id -> {name, thumbnail_url, price_from}
  const [allProducts, setAllProducts] = useState([]); // full product list w/ thumbnails for the picker
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [editingSlug, setEditingSlug] = useState(null);
  const [draft, setDraft] = useState({});
  const [picker, setPicker] = useState(null); // {slug, slot_key, current_ids: Set}
  const [pickerLoading, setPickerLoading] = useState(false);
  // {slug, slot_key, product_id, product_name, variants: [], selected: Set<int>}
  // — opens a small modal to restrict which sizes/variants of a product show
  //   inside a bundle slot. Empty selection = all variants allowed.
  const [variantEditor, setVariantEditor] = useState(null);
  const [creating, setCreating] = useState(false); // toggles the Create form

  const loadLivePrintfulProducts = async () => {
    setPickerLoading(true);
    try {
      const r = await fetch(`${backend}/api/admin/printful-products`, { headers: { "X-Admin-Token": token } });
      if (r.ok) {
        const d = await r.json();
        setAllProducts(d.products || []);
      }
    } catch (e) {
      // Fall back silently — the /api/shop/products list is already loaded.
      console.warn("BundlesPanel: live Printful refresh failed, using cached product list:", e);
    } finally {
      setPickerLoading(false);
    }
  };

  const load = async () => {
    setLoading(true); setErr("");
    try {
      const [ar, pr, spr] = await Promise.all([
        fetch(`${backend}/api/admin/bundles`, { headers: { "X-Admin-Token": token } }),
        fetch(`${backend}/api/bundles`),
        fetch(`${backend}/api/admin/printful-products`, { headers: { "X-Admin-Token": token } }),
      ]);
      if (!ar.ok) throw new Error(`admin HTTP ${ar.status}`);
      const ad = await ar.json();
      setBundles(ad.bundles || []);
      setSection(ad.section || {});
      if (pr.ok) {
        const pd = await pr.json();
        setCatalog(pd.catalog || {});
      }
      if (spr.ok) {
        const sd = await spr.json();
        setAllProducts(sd.products || []);
      } else {
        // Fallback to the public /shop/products list if the admin real-time endpoint fails
        try {
          const fb = await fetch(`${backend}/api/shop/products`);
          if (fb.ok) {
            const fd = await fb.json();
            setAllProducts(fd.products || []);
          }
        } catch (fbErr) {
          // Fallback fetch also failed — log so admins can debug, and let the
          // picker render empty (the outer catch already surfaces a user-visible error).
          console.warn("BundlesPanel: fallback /api/shop/products fetch failed:", fbErr);
        }
      }
    } catch (e) {
      setErr(e.message || "Load failed");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [token, backend]);// eslint-disable-line react-hooks/exhaustive-deps

  const flash = (s) => { setMsg(s); setTimeout(() => setMsg(""), 2200); };

  const createBundle = async (payload) => {
    setErr("");
    try {
      const r = await fetch(`${backend}/api/admin/bundles`, {
        method: "POST",
        headers: { "X-Admin-Token": token, "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.detail || `HTTP ${r.status}`);
      }
      await load();
      flash("Bundle created ✓ (hidden by default)");
      setCreating(false);
    } catch (e) {
      setErr(e.message || "Create failed");
    }
  };

  const deleteBundle = async (slug) => {
    if (!window.confirm(`Delete bundle "${slug}"? Existing merch orders keep their records — only the bundle itself is removed.`)) return;
    try {
      const r = await fetch(`${backend}/api/admin/bundles/${slug}`, {
        method: "DELETE",
        headers: { "X-Admin-Token": token },
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.detail || `HTTP ${r.status}`);
      }
      await load();
      flash(`Bundle "${slug}" deleted ✓`);
    } catch (e) {
      setErr(e.message || "Delete failed");
    }
  };

  const patchBundle = async (slug, patch) => {
    try {
      const r = await fetch(`${backend}/api/admin/bundles/${slug}`, {
        method: "PUT", headers: { "X-Admin-Token": token, "Content-Type": "application/json" },
        body: JSON.stringify(patch),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.detail || `HTTP ${r.status}`);
      }
      await load();
      flash("Bundle saved ✓");
    } catch (e) {
      setErr(e.message || "Save failed");
    }
  };

  const patchSection = async (patch) => {
    try {
      const r = await fetch(`${backend}/api/admin/bundle-section`, {
        method: "PUT", headers: { "X-Admin-Token": token, "Content-Type": "application/json" },
        body: JSON.stringify(patch),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const s = await r.json();
      setSection((prev) => ({ ...prev, ...s }));
      flash("Section saved ✓");
    } catch (e) {
      setErr(e.message || "Save failed");
    }
  };

  const move = async (slug, dir) => {
    const idx = bundles.findIndex((b) => b.slug === slug);
    const swap = idx + dir;
    if (idx < 0 || swap < 0 || swap >= bundles.length) return;
    const next = bundles.slice();
    [next[idx], next[swap]] = [next[swap], next[idx]];
    setBundles(next); // optimistic
    try {
      const r = await fetch(`${backend}/api/admin/bundles/reorder`, {
        method: "POST", headers: { "X-Admin-Token": token, "Content-Type": "application/json" },
        body: JSON.stringify({ order: next.map((b) => b.slug) }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      flash("Order saved ✓");
    } catch (e) {
      setErr(e.message || "Reorder failed");
      load();
    }
  };

  const startEdit = (bundle) => {
    setEditingSlug(bundle.slug);
    setDraft({
      name: bundle.name, badge: bundle.badge || "", price: bundle.price, description: bundle.description || "",
      cover_image_url: bundle.cover_image_url || "", free_gift_product_id: bundle.free_gift_product_id || "",
      coupon_code: bundle.coupon_code || "", mode: bundle.mode || "fixed_price",
      slot_edits: {}, // {slot_key: comma-separated pid string}
      // {slot_key: {product_id (str): [variant_id, ...]}} — persisted variant restrictions
      slot_variant_restrictions_edits: {},
    });
  };

  const commitEdit = async (slug) => {
    const patch = {
      name: draft.name, badge: draft.badge, price: Number(draft.price),
      description: draft.description, cover_image_url: draft.cover_image_url,
      coupon_code: (draft.coupon_code || "").toUpperCase().trim() || undefined,
      mode: draft.mode || undefined,
    };
    if (draft.free_gift_product_id) patch.free_gift_product_id = Number(draft.free_gift_product_id);
    if (Object.keys(draft.slot_edits || {}).length > 0) {
      patch.slot_eligible_products = Object.fromEntries(
        Object.entries(draft.slot_edits).map(([k, v]) => [k, v.split(",").map((s) => Number(s.trim())).filter(Boolean)])
      );
    }
    if (Object.keys(draft.slot_variant_restrictions_edits || {}).length > 0) {
      patch.slot_variant_restrictions = draft.slot_variant_restrictions_edits;
    }
    // Strip undefined so backend doesn't reject empty string
    Object.keys(patch).forEach((k) => patch[k] === undefined && delete patch[k]);
    await patchBundle(slug, patch);
    setEditingSlug(null); setDraft({});
  };

  // Save variant restrictions for a single (slug, slot, product) tuple immediately —
  // so the admin gets near-real-time feedback without having to hit the outer Save button.
  const saveVariantRestriction = async (slug, slot_key, product_id, variant_ids) => {
    const bundle = bundles.find((b) => b.slug === slug);
    const slot = (bundle?.slots || []).find((s) => s.key === slot_key);
    const currentRestrictions = { ...(slot?.variant_restrictions || {}) };
    const pidKey = String(product_id);
    if (!variant_ids || variant_ids.length === 0) {
      delete currentRestrictions[pidKey];
    } else {
      currentRestrictions[pidKey] = variant_ids.map(Number);
    }
    // Also mirror into draft so the outer form stays consistent if user is editing
    setDraft((d) => ({
      ...d,
      slot_variant_restrictions_edits: {
        ...(d.slot_variant_restrictions_edits || {}),
        [slot_key]: currentRestrictions,
      },
    }));
    await patchBundle(slug, { slot_variant_restrictions: { [slot_key]: currentRestrictions } });
  };

  return (
    <CollapsibleSection
      id="bundles"
      testId="admin-bundles-panel"
      title={<><Package size={20} weight="bold" className="text-[#FF1B91] inline mr-2" />Featured Legacy Bundles</>}
      subtitle={loading ? "Loading…" : `${bundles.length} bundle${bundles.length === 1 ? "" : "s"}`}
      onRefresh={load}
    >
      <div className="space-y-5">
      {err && <p data-testid="admin-bundles-err" className="text-red-400 text-sm border border-red-500/40 bg-red-500/10 p-3">{err}</p>}
      {msg && <p data-testid="admin-bundles-msg" className="text-emerald-400 text-sm border border-emerald-500/40 bg-emerald-500/10 p-3">{msg}</p>}

      <BundleSectionSettings section={section} setSection={setSection} bundles={bundles} patchSection={patchSection} />

      {/* Create New Bundle */}
      <div className="border border-white/15 bg-black p-4">
        <div className="flex items-center gap-3 flex-wrap">
          <button
            type="button"
            onClick={() => setCreating((c) => !c)}
            data-testid="admin-bundle-create-toggle"
            className="inline-flex items-center gap-2 bg-[#FF1B91] hover:bg-[#FF3FA3] text-white px-4 py-2 text-[10px] font-bold uppercase tracking-[0.25em]"
          >
            <Plus size={12} weight="bold" /> {creating ? "Close" : "Create New Bundle"}
          </button>
          <p className="text-white/50 text-xs">
            Add a brand-new bundle from scratch. It stays hidden by default so you can configure slots and preview before going live.
          </p>
        </div>
        {creating && <CreateBundleForm onSubmit={createBundle} onCancel={() => setCreating(false)} nextPosition={bundles.length} />}
      </div>

      {/* Bundle cards */}
      {loading ? (
        <p className="text-white/50 italic">Loading bundles…</p>
      ) : (
        <div className="space-y-3">
          {bundles.map((b, i) => {
            const emptySlots = (b.slots || []).filter((s) => (s.eligible_products || []).length === 0).length;
            const totalSlots = (b.slots || []).length;
            const isEditing = editingSlug === b.slug;
            return (
            <details key={b.slug} open={isEditing || emptySlots > 0} data-testid={`admin-bundle-row-${b.slug}`} className={`border border-white/15 bg-black ${b.hidden ? "opacity-60" : ""}`}>
              <summary className="cursor-pointer p-4 flex items-start gap-3 flex-wrap hover:bg-white/[0.02]">
                <div className="flex flex-col gap-1">
                  <button type="button" onClick={(e) => { e.preventDefault(); move(b.slug, -1); }} disabled={i === 0} aria-label="Move up" data-testid={`admin-bundle-up-${b.slug}`}
                    className="w-8 h-8 flex items-center justify-center border border-white/20 hover:border-[#FF1B91] disabled:opacity-30">
                    <ArrowUp size={12} weight="bold" />
                  </button>
                  <button type="button" onClick={(e) => { e.preventDefault(); move(b.slug, 1); }} disabled={i === bundles.length - 1} aria-label="Move down" data-testid={`admin-bundle-down-${b.slug}`}
                    className="w-8 h-8 flex items-center justify-center border border-white/20 hover:border-[#FF1B91] disabled:opacity-30">
                    <ArrowDown size={12} weight="bold" />
                  </button>
                </div>
                <div className="flex-1 min-w-[240px]">
                  <p className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#FF1B91] flex items-center gap-2">
                    <span data-testid={`admin-bundle-position-${b.slug}`} className="bg-[#FF1B91] text-white px-2 py-0.5 font-bold">Pos {i + 1}</span>
                    {b.badge || "—"}
                  </p>
                  <p className="font-display font-black uppercase tracking-tight text-lg">{b.name}</p>
                  <p className="text-white/60 text-xs mt-1 flex items-center gap-2 flex-wrap">
                    <span>${Number(b.price).toFixed(2)}</span>
                    <span>·</span>
                    <span>{totalSlots} slots</span>
                    <span>·</span>
                    <span>free gift #{b.free_gift_product_id}</span>
                    {emptySlots > 0 && (
                      <span data-testid={`admin-bundle-empty-slots-${b.slug}`} className="ml-1 bg-yellow-500/20 text-yellow-400 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide">
                        ⚠ {emptySlots} empty step{emptySlots === 1 ? "" : "s"} — no next choice
                      </span>
                    )}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button type="button" onClick={(e) => { e.preventDefault(); patchBundle(b.slug, { hidden: !b.hidden }); }}
                    data-testid={`admin-bundle-toggle-hidden-${b.slug}`}
                    className={`inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] font-bold px-3 py-2 border ${b.hidden ? "border-red-500 text-red-400" : "border-white/20 text-white/70 hover:border-[#FF1B91]"}`}>
                    {b.hidden ? <><EyeSlash size={12} weight="bold" /> Hidden</> : <><Eye size={12} weight="bold" /> Visible</>}
                  </button>
                  <button type="button" onClick={(e) => { e.preventDefault(); (isEditing ? setEditingSlug(null) : startEdit(b)); }}
                    data-testid={`admin-bundle-edit-${b.slug}`}
                    className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] font-bold px-3 py-2 border border-white/20 hover:border-[#FF1B91]">
                    <PencilSimple size={12} weight="bold" /> {isEditing ? "Cancel" : "Edit"}
                  </button>
                  <button type="button" onClick={(e) => { e.preventDefault(); deleteBundle(b.slug); }}
                    data-testid={`admin-bundle-delete-${b.slug}`}
                    className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] font-bold px-3 py-2 border border-red-500/40 text-red-400 hover:bg-red-500/10">
                    <Trash size={12} weight="bold" /> Delete
                  </button>
                </div>
              </summary>
              <div className="px-4 pb-4">
              {isEditing && (
                <div className="mt-4 grid gap-3 border-t border-white/10 pt-4">
                  <div className="grid md:grid-cols-2 gap-3">
                    <input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                      placeholder="Name" className="bg-black border-2 border-white/20 text-white px-3 py-2 text-sm"
                      data-testid={`admin-bundle-name-${b.slug}`} />
                    <input value={draft.badge} onChange={(e) => setDraft({ ...draft, badge: e.target.value })}
                      placeholder="Badge" className="bg-black border-2 border-white/20 text-white px-3 py-2 text-sm" />
                    <input type="number" step="0.01" value={draft.price} onChange={(e) => setDraft({ ...draft, price: e.target.value })}
                      placeholder="Price" className="bg-black border-2 border-white/20 text-white px-3 py-2 text-sm"
                      data-testid={`admin-bundle-price-${b.slug}`} />
                    <input value={draft.free_gift_product_id} onChange={(e) => setDraft({ ...draft, free_gift_product_id: e.target.value })}
                      placeholder="Free gift product ID (default 445826587 = sticker)" className="bg-black border-2 border-white/20 text-white px-3 py-2 text-sm font-mono" />
                    <label className="block space-y-1">
                      <span className="text-[10px] uppercase tracking-[0.25em] font-bold text-white/70">Auto-coupon code</span>
                      <input
                        value={draft.coupon_code ?? b.coupon_code ?? ""}
                        onChange={(e) => setDraft({ ...draft, coupon_code: e.target.value.toUpperCase() })}
                        placeholder="LITTLELEGACY"
                        data-testid={`admin-bundle-coupon-${b.slug}`}
                        className="w-full bg-black border-2 border-white/20 text-white px-3 py-2 text-sm font-mono uppercase"
                      />
                    </label>
                    <label className="block space-y-1">
                      <span className="text-[10px] uppercase tracking-[0.25em] font-bold text-white/70">Pricing mode</span>
                      <select
                        value={draft.mode ?? b.mode ?? "fixed_price"}
                        onChange={(e) => setDraft({ ...draft, mode: e.target.value })}
                        data-testid={`admin-bundle-mode-${b.slug}`}
                        className="w-full bg-black border-2 border-white/20 text-white px-3 py-2 text-sm"
                      >
                        <option value="fixed_price">Fixed price</option>
                        <option value="lowest_free">Pick 5, lowest FREE</option>
                      </select>
                    </label>
                  </div>
                  <textarea rows={2} value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                    placeholder="Description" className="bg-black border-2 border-white/20 text-white px-3 py-2 text-sm" />
                  <div className="border border-white/15 bg-black p-3 space-y-2" data-testid={`bundle-cover-editor-${b.slug}`}>
                    <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-white/70">
                      Customer-facing bundle image
                    </p>
                    <p className="text-[10px] text-white/45 leading-relaxed -mt-1">
                      Recommended: 1200×900 (4:3), JPG/PNG/WebP. Paste any hosted image URL. Leave blank to fall back to the auto-generated product collage. Products, pricing, steps and checkout are unaffected.
                    </p>
                    <div className="flex gap-3 items-start">
                      <div className="w-24 aspect-[4/3] flex-shrink-0 border border-white/20 bg-[#141414] overflow-hidden">
                        {draft.cover_image_url ? (
                          <img
                            src={draft.cover_image_url}
                            alt="Bundle cover preview"
                            data-testid={`bundle-cover-preview-${b.slug}`}
                            className="w-full h-full object-cover"
                            onError={(e) => { e.currentTarget.style.display = "none"; }}
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-[9px] uppercase tracking-[0.2em] text-white/35 text-center px-1">
                            Auto collage
                          </div>
                        )}
                      </div>
                      <div className="flex-1 space-y-2">
                        <input
                          value={draft.cover_image_url}
                          onChange={(e) => setDraft({ ...draft, cover_image_url: e.target.value })}
                          data-testid={`bundle-cover-url-${b.slug}`}
                          placeholder="Paste image URL (https://…)"
                          className="w-full bg-black border-2 border-white/20 text-white px-3 py-2 text-sm font-mono"
                        />
                        {draft.cover_image_url && (
                          <button
                            type="button"
                            onClick={() => setDraft({ ...draft, cover_image_url: "" })}
                            data-testid={`bundle-cover-clear-${b.slug}`}
                            className="text-[10px] uppercase tracking-[0.25em] font-bold border border-red-500/50 hover:bg-red-500/20 text-red-300 hover:text-red-200 px-3 py-1.5"
                          >
                            Remove image · Revert to auto collage
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Slot editor with visual product picker */}
                  <div className="border border-white/10 p-3 space-y-3">
                    <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-white/70">Eligible Printful products per slot · click a thumbnail to open the picker · Pick products button opens the visual selector</p>
                    {(b.slots || []).map((s, slotIdx) => {
                      const draftIds = draft.slot_edits?.[s.key] !== undefined
                        ? draft.slot_edits[s.key].split(",").map(Number).filter(Boolean)
                        : (s.eligible_products || []);
                      const isEmpty = draftIds.length === 0;
                      const slots = b.slots || [];
                      const nextSlot = slots[slotIdx + 1];
                      return (
                        <details key={s.key} open={isEmpty || slotIdx === 0} data-testid={`admin-bundle-slot-details-${b.slug}-${s.key}`} className={`border-l-2 ${isEmpty ? "border-yellow-500" : "border-emerald-500"} pl-3`}>
                          <summary className="cursor-pointer flex items-center gap-2 flex-wrap py-1">
                            <span className={`text-[10px] uppercase tracking-[0.2em] font-bold flex-1 min-w-[160px] ${isEmpty ? "text-yellow-400" : "text-white"}`}>
                              Step {slotIdx + 1} · {s.label}
                            </span>
                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${isEmpty ? "bg-yellow-500/20 text-yellow-400" : "bg-emerald-500/20 text-emerald-400"}`}>
                              {isEmpty ? "⚠ EMPTY — no next choice for customer" : `${draftIds.length} product${draftIds.length === 1 ? "" : "s"}`}
                            </span>
                            {s.required_qty > 1 && (
                              <span className="text-[10px] uppercase text-white/50 font-bold">Customer picks {s.required_qty}</span>
                            )}
                          </summary>
                          <div className="pt-3 space-y-2">
                            <div className="flex items-center gap-2 flex-wrap">
                              <button
                                type="button"
                                onClick={() => setPicker({ slug: b.slug, slot_key: s.key, slot_label: s.label, slot_index: slotIdx, all_slot_keys: slots.map((x) => ({ key: x.key, label: x.label })), current_ids: new Set(draftIds) })}
                                data-testid={`admin-bundle-slot-picker-${b.slug}-${s.key}`}
                                className="text-[10px] uppercase tracking-[0.2em] font-bold bg-[#FF1B91] text-white hover:bg-[#FF3FA3] px-3 py-1.5"
                              >
                                Pick products ({draftIds.length}) →
                              </button>
                              {nextSlot && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    // Jump the picker directly to the next slot so the admin can
                                    // walk through the whole bundle without closing/reopening.
                                    const nextDraftIds = draft.slot_edits?.[nextSlot.key] !== undefined
                                      ? draft.slot_edits[nextSlot.key].split(",").map(Number).filter(Boolean)
                                      : (nextSlot.eligible_products || []);
                                    setPicker({ slug: b.slug, slot_key: nextSlot.key, slot_label: nextSlot.label, slot_index: slotIdx + 1, all_slot_keys: slots.map((x) => ({ key: x.key, label: x.label })), current_ids: new Set(nextDraftIds) });
                                  }}
                                  data-testid={`admin-bundle-slot-next-${b.slug}-${s.key}`}
                                  className="text-[10px] uppercase tracking-[0.2em] font-bold border border-white/20 text-white/70 hover:border-[#FF1B91] px-3 py-1.5"
                                >
                                  Skip to next step →
                                </button>
                              )}
                            </div>
                            {/* Larger thumbnails with product name so admin can review at a glance */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                              {draftIds.map((pid) => {
                                const prod = catalog[pid] || catalog[String(pid)] || allProducts.find((p) => Number(p.id) === Number(pid));
                                const img = prod?.thumbnail_url || "";
                                const name = prod?.name || `#${pid}`;
                                const totalVariants = prod?.variants?.length || 0;
                                const restrictionMap = s.variant_restrictions || {};
                                const allowedIds = restrictionMap[String(pid)] || restrictionMap[pid] || [];
                                const restrictedCount = allowedIds.length;
                                const sizeLabel = restrictedCount === 0
                                  ? `All${totalVariants ? ` (${totalVariants})` : ""}`
                                  : `${restrictedCount} of ${totalVariants}`;
                                return (
                                  <div key={pid} data-testid={`admin-slot-thumb-${b.slug}-${s.key}-${pid}`} className="flex items-center gap-3 border border-white/15 bg-[#0A0A0A] p-2" title={name}>
                                    <div className="w-14 h-14 flex-shrink-0 bg-black border border-white/10 overflow-hidden">
                                      {img ? <img src={img} alt={name} className="w-full h-full object-cover" /> : null}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <p className="text-white text-[11px] font-bold leading-tight line-clamp-2 break-words">{name}</p>
                                      <p className="text-white/40 text-[10px] font-mono mt-0.5">#{pid}</p>
                                      {totalVariants > 1 && prod?.variants && (
                                        <button
                                          type="button"
                                          data-testid={`admin-slot-sizes-${b.slug}-${s.key}-${pid}`}
                                          onClick={() => setVariantEditor({
                                            slug: b.slug,
                                            slot_key: s.key,
                                            slot_label: s.label,
                                            product_id: pid,
                                            product_name: name,
                                            variants: prod.variants,
                                            selected: new Set(allowedIds.map(Number)),
                                          })}
                                          className={`mt-1 inline-flex items-center gap-1 text-[9px] uppercase tracking-[0.15em] font-bold px-2 py-1 border ${restrictedCount > 0 ? "border-[#FF1B91] text-[#FF1B91]" : "border-white/25 text-white/60 hover:border-white/50"}`}
                                        >
                                          Sizes: {sizeLabel}
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                              {isEmpty && (
                                <p className="text-yellow-400 text-xs italic border border-yellow-500/40 bg-yellow-500/5 p-2 col-span-full">
                                  ⚠ No products in this step — customers will not see a &ldquo;next choice&rdquo; here. Click <strong>Pick products</strong> to add at least one Printful product.
                                </p>
                              )}
                            </div>
                          </div>
                        </details>
                      );
                    })}
                  </div>

                  <div className="flex items-center gap-2">
                    <button type="button" onClick={() => commitEdit(b.slug)} data-testid={`admin-bundle-save-${b.slug}`}
                      className="inline-flex items-center gap-2 bg-[#FF1B91] hover:bg-[#FF3FA3] text-white px-4 py-2 text-[10px] font-bold uppercase tracking-[0.25em]">
                      <Check size={12} weight="bold" /> Save changes
                    </button>
                    <button type="button" onClick={() => { setEditingSlug(null); setDraft({}); }}
                      className="inline-flex items-center gap-2 text-white/60 hover:text-white text-[10px] uppercase tracking-[0.25em] font-bold px-3 py-2 border border-white/15">
                      <Prohibit size={12} weight="bold" /> Cancel
                    </button>
                  </div>
                </div>
              )}
              {!isEditing && (
                <div className="space-y-2 py-1">
                  <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-white/60">All steps at a glance:</p>
                  <div className="grid gap-1.5">
                    {(b.slots || []).map((s, slotIdx) => {
                      const ids = s.eligible_products || [];
                      const isEmpty = ids.length === 0;
                      return (
                        <div key={s.key} className={`flex items-center gap-3 border-l-2 pl-3 py-1 ${isEmpty ? "border-yellow-500" : "border-emerald-500"}`}>
                          <span className="text-[10px] uppercase tracking-[0.2em] text-white/70 font-bold min-w-[180px]">Step {slotIdx + 1} · {s.label}</span>
                          <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${isEmpty ? "bg-yellow-500/20 text-yellow-400" : "bg-emerald-500/20 text-emerald-400"}`}>
                            {isEmpty ? "⚠ EMPTY" : `${ids.length} product${ids.length === 1 ? "" : "s"}`}
                          </span>
                          {!isEmpty && (
                            <div className="flex gap-1 flex-wrap">
                              {ids.slice(0, 5).map((pid) => {
                                const prod = catalog[pid] || catalog[String(pid)] || allProducts.find((p) => Number(p.id) === Number(pid));
                                return prod?.thumbnail_url ? (
                                  <div key={pid} className="w-8 h-8 bg-black border border-white/10 overflow-hidden" title={prod?.name || `#${pid}`}>
                                    <img src={prod.thumbnail_url} alt="" className="w-full h-full object-cover" />
                                  </div>
                                ) : null;
                              })}
                              {ids.length > 5 && <span className="text-[10px] text-white/40 self-center">+{ids.length - 5}</span>}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              </div>
            </details>
            );
          })}
        </div>
      )}

      {picker && (
        <ProductPicker
          slotLabel={picker.slot_label}
          slotIndex={picker.slot_index}
          allSlotKeys={picker.all_slot_keys}
          allProducts={allProducts}
          initialIds={picker.current_ids}
          loading={pickerLoading}
          onRefresh={loadLivePrintfulProducts}
          onCancel={() => setPicker(null)}
          onSave={(ids, goNext) => {
            setDraft((d) => ({ ...d, slot_edits: { ...(d.slot_edits || {}), [picker.slot_key]: ids.join(",") } }));
            if (goNext && picker.all_slot_keys && picker.all_slot_keys[picker.slot_index + 1]) {
              // Jump to the next slot inside the same modal — no close/reopen.
              const nextSlot = picker.all_slot_keys[picker.slot_index + 1];
              const b = bundles.find((x) => x.slug === picker.slug);
              const nextIds = draft.slot_edits?.[nextSlot.key] !== undefined
                ? draft.slot_edits[nextSlot.key].split(",").map(Number).filter(Boolean)
                : ((b?.slots || []).find((s) => s.key === nextSlot.key)?.eligible_products || []);
              setPicker({ ...picker, slot_key: nextSlot.key, slot_label: nextSlot.label, slot_index: picker.slot_index + 1, current_ids: new Set(nextIds) });
            } else {
              setPicker(null);
            }
          }}
        />
      )}
      {variantEditor && (
        <VariantRestrictionModal
          editor={variantEditor}
          onCancel={() => setVariantEditor(null)}
          onSave={async (selectedIds) => {
            await saveVariantRestriction(variantEditor.slug, variantEditor.slot_key, variantEditor.product_id, selectedIds);
            setVariantEditor(null);
          }}
        />
      )}
      </div>
    </CollapsibleSection>
  );
}

function VariantRestrictionModal({ editor, onCancel, onSave }) {
  const [selected, setSelected] = useState(new Set(editor.selected || new Set()));
  const toggle = (vid) => {
    const next = new Set(selected);
    if (next.has(vid)) next.delete(vid); else next.add(vid);
    setSelected(next);
  };
  const clear = () => setSelected(new Set());
  const selectAll = () => setSelected(new Set(editor.variants.map((v) => Number(v.id))));

  return (
    <div data-testid="admin-variant-editor" className="fixed inset-0 z-[70] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-[#0F0F0F] border border-white/15 w-full max-w-3xl max-h-[90vh] flex flex-col">
        <div className="p-4 border-b border-white/10">
          <p className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#FF1B91]">Restrict sizes · {editor.slot_label}</p>
          <p className="font-display font-black uppercase tracking-tight text-lg text-white mt-1">{editor.product_name}</p>
          <p className="text-white/60 text-xs mt-2 leading-relaxed">
            Check the variants you want customers to choose from inside this bundle slot. <strong className="text-white">Uncheck all to allow every variant</strong> (default behavior).
          </p>
        </div>
        <div className="p-4 flex items-center gap-2 border-b border-white/10">
          <button type="button" onClick={selectAll} className="text-[10px] uppercase tracking-[0.2em] font-bold border border-white/20 hover:border-white/50 text-white/80 px-3 py-1.5">Select all</button>
          <button type="button" onClick={clear} className="text-[10px] uppercase tracking-[0.2em] font-bold border border-white/20 hover:border-white/50 text-white/80 px-3 py-1.5">Clear (allow all)</button>
          <span className="text-white/50 text-[10px] uppercase tracking-[0.2em] ml-auto">{selected.size} of {editor.variants.length} picked</span>
        </div>
        <div className="p-4 overflow-y-auto flex-1">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
            {editor.variants.map((v) => {
              const vid = Number(v.id);
              const on = selected.has(vid);
              return (
                <button
                  type="button"
                  key={vid}
                  onClick={() => toggle(vid)}
                  data-testid={`admin-variant-toggle-${vid}`}
                  className={`text-left border-2 bg-[#0A0A0A] p-2 transition-colors ${on ? "border-[#FF1B91] bg-[#FF1B91]/5" : "border-white/15 hover:border-white/40"}`}
                >
                  <div className="aspect-square bg-black overflow-hidden mb-2">
                    {v.preview_url && <img src={v.preview_url} alt={v.name || "variant"} className="w-full h-full object-cover" />}
                  </div>
                  <p className="text-white text-[10px] font-bold leading-tight line-clamp-2">{v.name}</p>
                  <p className="text-white/40 text-[9px] font-mono mt-0.5">#{vid}</p>
                </button>
              );
            })}
          </div>
        </div>
        <div className="p-4 border-t border-white/10 flex items-center gap-2 justify-end">
          <button type="button" onClick={onCancel} className="text-[10px] uppercase tracking-[0.25em] font-bold px-4 py-2 border border-white/20 hover:border-white/50 text-white/80">Cancel</button>
          <button
            type="button"
            data-testid="admin-variant-save"
            onClick={() => onSave(Array.from(selected))}
            className="text-[10px] uppercase tracking-[0.25em] font-bold px-4 py-2 bg-[#FF1B91] hover:bg-[#FF3FA3] text-white"
          >
            Save restriction
          </button>
        </div>
      </div>
    </div>
  );
}


function CreateBundleForm({ onSubmit, onCancel, nextPosition }) {
  const [draft, setDraft] = useState({
    slug: "",
    name: "",
    price: "29.99",
    description: "",
    badge: "",
    cover_image_url: "",
    coupon_code: "",
    mode: "fixed_price",
    hidden: true,
    free_gift_product_id: "",
    order: nextPosition,
  });
  const [saving, setSaving] = useState(false);
  const [localErr, setLocalErr] = useState("");

  const slugify = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

  const submit = async () => {
    setLocalErr("");
    const slug = draft.slug || slugify(draft.name);
    if (!slug) { setLocalErr("Slug or name is required"); return; }
    if (!draft.name.trim()) { setLocalErr("Name is required"); return; }
    if (draft.price === "" || Number.isNaN(Number(draft.price))) { setLocalErr("Price is required"); return; }
    setSaving(true);
    try {
      await onSubmit({
        slug,
        name: draft.name.trim(),
        price: Number(draft.price),
        description: draft.description,
        badge: draft.badge,
        cover_image_url: draft.cover_image_url,
        coupon_code: draft.coupon_code,
        mode: draft.mode,
        hidden: !!draft.hidden,
        free_gift_product_id: draft.free_gift_product_id ? Number(draft.free_gift_product_id) : undefined,
        order: Number(draft.order),
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="admin-bundle-create-form" className="mt-4 grid gap-3 border-t border-white/10 pt-4">
      {localErr && <p data-testid="admin-bundle-create-err" className="text-red-400 text-sm border border-red-500/40 bg-red-500/10 p-3">{localErr}</p>}
      <div className="grid md:grid-cols-2 gap-3">
        <label className="block space-y-1">
          <span className="text-[10px] uppercase tracking-[0.25em] font-bold text-white/70">Bundle name</span>
          <input
            value={draft.name}
            onChange={(e) => setDraft({ ...draft, name: e.target.value, slug: draft.slug || slugify(e.target.value) })}
            placeholder="Ultimate Warrior Pack"
            data-testid="admin-bundle-create-name"
            className="w-full bg-black border-2 border-white/20 text-white px-3 py-2 text-sm"
          />
        </label>
        <label className="block space-y-1">
          <span className="text-[10px] uppercase tracking-[0.25em] font-bold text-white/70">URL slug</span>
          <input
            value={draft.slug}
            onChange={(e) => setDraft({ ...draft, slug: slugify(e.target.value) })}
            placeholder="ultimate-warrior-pack"
            data-testid="admin-bundle-create-slug"
            className="w-full bg-black border-2 border-white/20 text-white px-3 py-2 text-sm font-mono"
          />
        </label>
        <label className="block space-y-1">
          <span className="text-[10px] uppercase tracking-[0.25em] font-bold text-white/70">Price (USD)</span>
          <input
            type="number" step="0.01"
            value={draft.price}
            onChange={(e) => setDraft({ ...draft, price: e.target.value })}
            data-testid="admin-bundle-create-price"
            className="w-full bg-black border-2 border-white/20 text-white px-3 py-2 text-sm"
          />
        </label>
        <label className="block space-y-1">
          <span className="text-[10px] uppercase tracking-[0.25em] font-bold text-white/70">Badge (optional)</span>
          <input
            value={draft.badge}
            onChange={(e) => setDraft({ ...draft, badge: e.target.value })}
            placeholder="Best value · Featured"
            data-testid="admin-bundle-create-badge"
            className="w-full bg-black border-2 border-white/20 text-white px-3 py-2 text-sm"
          />
        </label>
        <label className="block space-y-1">
          <span className="text-[10px] uppercase tracking-[0.25em] font-bold text-white/70">Display position (0 = first)</span>
          <input
            type="number" min="0"
            value={draft.order}
            onChange={(e) => setDraft({ ...draft, order: e.target.value })}
            data-testid="admin-bundle-create-order"
            className="w-full bg-black border-2 border-white/20 text-white px-3 py-2 text-sm"
          />
        </label>
        <label className="block space-y-1">
          <span className="text-[10px] uppercase tracking-[0.25em] font-bold text-white/70">Pricing mode</span>
          <select
            value={draft.mode}
            onChange={(e) => setDraft({ ...draft, mode: e.target.value })}
            data-testid="admin-bundle-create-mode"
            className="w-full bg-black border-2 border-white/20 text-white px-3 py-2 text-sm"
          >
            <option value="fixed_price">Fixed price</option>
            <option value="lowest_free">Pick 5 · Lowest FREE</option>
          </select>
        </label>
        <label className="block space-y-1">
          <span className="text-[10px] uppercase tracking-[0.25em] font-bold text-white/70">Free gift product ID (blank = sticker default)</span>
          <input
            value={draft.free_gift_product_id}
            onChange={(e) => setDraft({ ...draft, free_gift_product_id: e.target.value })}
            placeholder="445826587"
            data-testid="admin-bundle-create-freegift"
            className="w-full bg-black border-2 border-white/20 text-white px-3 py-2 text-sm font-mono"
          />
        </label>
        <label className="block space-y-1">
          <span className="text-[10px] uppercase tracking-[0.25em] font-bold text-white/70">Auto-coupon code (optional)</span>
          <input
            value={draft.coupon_code}
            onChange={(e) => setDraft({ ...draft, coupon_code: e.target.value.toUpperCase() })}
            placeholder="WARRIOR10"
            data-testid="admin-bundle-create-coupon"
            className="w-full bg-black border-2 border-white/20 text-white px-3 py-2 text-sm font-mono uppercase"
          />
        </label>
      </div>
      <label className="block space-y-1">
        <span className="text-[10px] uppercase tracking-[0.25em] font-bold text-white/70">Description</span>
        <textarea
          rows={2}
          value={draft.description}
          onChange={(e) => setDraft({ ...draft, description: e.target.value })}
          data-testid="admin-bundle-create-description"
          className="w-full bg-black border-2 border-white/20 text-white px-3 py-2 text-sm"
        />
      </label>
      <label className="block space-y-1">
        <span className="text-[10px] uppercase tracking-[0.25em] font-bold text-white/70">Bundle image (optional — recommended 1200×900 · 4:3; blank uses auto-collage)</span>
        <div className="flex gap-3 items-start">
          <div className="w-20 aspect-[4/3] flex-shrink-0 border border-white/20 bg-[#141414] overflow-hidden">
            {draft.cover_image_url ? (
              <img
                src={draft.cover_image_url}
                alt="Bundle preview"
                data-testid="admin-bundle-create-cover-preview"
                className="w-full h-full object-cover"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-[9px] uppercase tracking-[0.2em] text-white/35 text-center px-1">
                Auto collage
              </div>
            )}
          </div>
          <div className="flex-1 space-y-2">
            <input
              value={draft.cover_image_url}
              onChange={(e) => setDraft({ ...draft, cover_image_url: e.target.value })}
              data-testid="admin-bundle-create-cover"
              placeholder="Paste image URL (https://…)"
              className="w-full bg-black border-2 border-white/20 text-white px-3 py-2 text-sm font-mono"
            />
            {draft.cover_image_url && (
              <button
                type="button"
                onClick={() => setDraft({ ...draft, cover_image_url: "" })}
                data-testid="admin-bundle-create-cover-clear"
                className="text-[10px] uppercase tracking-[0.25em] font-bold border border-red-500/50 hover:bg-red-500/20 text-red-300 px-3 py-1.5"
              >
                Remove
              </button>
            )}
          </div>
        </div>
      </label>
      <label className="flex items-center gap-2 text-white/80 text-sm">
        <input
          type="checkbox"
          checked={!draft.hidden}
          onChange={(e) => setDraft({ ...draft, hidden: !e.target.checked })}
          data-testid="admin-bundle-create-active"
          className="w-4 h-4 accent-[#FF1B91]"
        />
        Publish immediately (make visible on /shop) — leave OFF to save as inactive draft.
      </label>
      <p className="text-white/40 text-[11px] italic">
        After creating, use the Edit button to configure product slots. New bundles start with no slots — customers won&apos;t see it until at least one slot has products AND you toggle it visible.
      </p>
      <div className="flex items-center gap-2">
        <button type="button" onClick={submit} disabled={saving} data-testid="admin-bundle-create-submit"
          className="inline-flex items-center gap-2 bg-[#FF1B91] hover:bg-[#FF3FA3] text-white px-4 py-2 text-[10px] font-bold uppercase tracking-[0.25em] disabled:opacity-40">
          <Check size={12} weight="bold" /> {saving ? "Creating…" : "Create bundle"}
        </button>
        <button type="button" onClick={onCancel} className="inline-flex items-center gap-2 text-white/60 hover:text-white text-[10px] uppercase tracking-[0.25em] font-bold px-3 py-2 border border-white/15">
          <Prohibit size={12} weight="bold" /> Cancel
        </button>
      </div>
    </div>
  );
}
